<template>
  <div class="kb-fault-case-detail">
    <!-- 导航提示标题 -->
    <ml-tip :showHeader="false" content="典型案例详情" />
    <ml-tip class="scroll-box" :showHeader="false" style="border: 0">
      <el-scrollbar :height="tabberHeight(185)">
        <div style="width: 900px; margin: 20px auto">
          <!-- ==================== 基础信息 ==================== -->
          <el-divider content-position="left" style="font-weight: bold">基础信息</el-divider>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="故障标题">
              {{ detailData.faultTitle || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="一级分类">
              {{ detailData.firstClassify || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="故障等级">
              <el-tag :type="levelTagType" size="small">
                {{ detailData.faultLevel || '-' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="涉及设备/系统">
              {{ detailData.targetDevice || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="故障发生时间">
              {{ detailData.faultTime || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="自定义标签">
              <template v-if="detailData.customTags">
                <el-tag
                  v-for="(tag, i) in detailData.customTags.split(',')"
                  :key="i"
                  size="small"
                  style="margin-right: 6px"
                >
                  {{ tag }}
                </el-tag>
              </template>
              <span v-else>-</span>
            </el-descriptions-item>
          </el-descriptions>

          <!-- ==================== 背景与现象 ==================== -->
          <el-divider content-position="left" style="font-weight: bold">背景与现象</el-divider>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="故障前置背景">
              <pre class="detail-pre">{{ detailData.preBackground || '-' }}</pre>
            </el-descriptions-item>
            <el-descriptions-item label="故障现场现象">
              <pre class="detail-pre">{{ detailData.scenePhenomenon || '-' }}</pre>
            </el-descriptions-item>
            <el-descriptions-item label="截图/现场照片">
              <template v-if="detailData.imgUrlList && detailData.imgUrlList.length">
                <ml-list
                  fileName="fileName"
                  :list="detailData.imgUrlList"
                  :showDelete="false"
                  :showDown="true"
                  :showPreview="true"
                />
              </template>
              <span v-else>-</span>
            </el-descriptions-item>
          </el-descriptions>

          <!-- ==================== 排查处理过程 ==================== -->
          <el-divider content-position="left" style="font-weight: bold">排查处理过程</el-divider>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="分步排查思路">
              <pre class="detail-pre">{{ detailData.checkThought || '-' }}</pre>
            </el-descriptions-item>
            <el-descriptions-item label="最终处置操作">
              <pre class="detail-pre">{{ detailData.handleOperation || '-' }}</pre>
            </el-descriptions-item>
          </el-descriptions>

          <!-- ==================== 复盘与附件 ==================== -->
          <el-divider content-position="left" style="font-weight: bold">复盘与附件</el-divider>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="故障根本原因">
              <pre class="detail-pre">{{ detailData.rootCause || '-' }}</pre>
            </el-descriptions-item>
            <el-descriptions-item label="预防优化方案">
              <pre class="detail-pre">{{ detailData.preventPlan || '-' }}</pre>
            </el-descriptions-item>
            <el-descriptions-item label="附件">
              <template v-if="detailData.fileUrlList && detailData.fileUrlList.length">
                <ml-list
                  fileName="fileName"
                  :list="detailData.fileUrlList"
                  :showDelete="false"
                  :showDown="true"
                  :showPreview="true"
                />
              </template>
              <span v-else>-</span>
            </el-descriptions-item>
          </el-descriptions>

          <!-- 其他信息 -->
          <el-divider content-position="left" style="font-weight: bold">其他信息</el-divider>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="案例编号">
              {{ detailData.caseId || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag :type="detailData.validStatus === '1' ? 'success' : 'info'" size="small">
                {{ detailData.validStatus === '1' ? '已入库' : '草稿' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="创建时间">
              {{ detailData.createTime || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="更新时间">
              {{ detailData.updateTime || '-' }}
            </el-descriptions-item>
          </el-descriptions>

          <!-- 返回按钮 -->
          <div style="text-align: center; margin-top: 30px">
            <el-button type="primary" icon="el-icon-back" @click="goBack">返回列表</el-button>
            <el-button
              type="warning"
              icon="el-icon-edit"
              @click="goEdit"
              v-if="detailData.validStatus !== '1'"
              >编辑</el-button
            >
          </div>
        </div>
      </el-scrollbar>
    </ml-tip>
  </div>
</template>

<script>
import { computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { tabberHeight } from '@/utils'

export default {
  name: 'KbFaultCaseDetail',
  setup() {
    const { dispatch } = useStore()
    const route = useRoute()
    const router = useRouter()
    const { caseId } = route.query

    const detailData = reactive({})

    // 故障等级对应的 tag 样式
    const levelTagType = computed(() => {
      const map = {
        轻微: 'info',
        一般: '',
        严重: 'warning',
        灾难: 'danger'
      }
      return map[detailData.faultLevel] || 'info'
    })

    // 获取详情
    const getDetail = async () => {
      if (!caseId) return
      const res = await dispatch('fetchGetKbFaultCaseInfo', caseId)
      if (res && res.code === 200 && res.data) {
        Object.assign(detailData, res.data)
      }
    }

    // 返回列表
    const goBack = () => {
      router.push({ path: '/kbFaultCase' })
    }

    // 去编辑
    const goEdit = () => {
      router.push({ path: '/kbFaultCaseEdit', query: { caseId } })
    }

    onMounted(() => {
      getDetail()
    })

    return {
      detailData,
      levelTagType,
      goBack,
      goEdit,
      tabberHeight
    }
  }
}
</script>

<style lang="scss" scoped>
.kb-fault-case-detail {
  @extend %params-global;
  .detail-pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
    font-family: inherit;
    line-height: 1.6;
  }
  ::v-deep .el-descriptions__label.is-bordered-label {
    white-space: nowrap;
    width: 120px;
  }
}
</style>
